import React, { Fragment, useCallback, useEffect } from 'react';
import { makeStyles } from '@material-ui/core';
import Grid from '@material-ui/core/Grid/Grid';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import { useHistory } from 'react-router-dom';

import { Routers } from '../config/constants';
import locales from '../locales';
import PageHeaderWrap from '../components/Common/PageHeaderWrap';
import PageContentWrap from '../components/Common/PageContentWrap';
import ReservesSystemInfo from '../containers/ReservesSystemInfo';
import RestaurantCard from "../containers/RestaurantCard";
import ReservesSystemSettings from '../containers/ReservesSystemSettings';
import ReservesSystemTables from '../containers/ReservesSystemTables';
import ReservesSystemAdditionalNotifications from '../containers/ReservesSystemAdditionalNotifications';
import ReservesSystemAutoBooking from '../containers/ReservesSystemAutoBooking';
import ReservesSystemSchema from '../containers/ReservesSystemSchema';
import CashBoxWarningCont from '../containers/CashBoxWarning';
import ReservesSystemWidgets from '../containers/ReservesSystemWidgets';
import EmployeesPage from './EmployeesPage';
import ProfilePage from './ProfilePage';
import { NoHostessWarning } from '../features';
import { Employees } from '../entities';
import RestaurantActions from "../components/RestaurantSettings/RestaurantActions";
import { PrepaySettings } from '../_FSD/pages/prepaySettings';

const useStyles = makeStyles({
  tabsRoot: {
    minHeight: 64,
  },
  tabRoot: {
    minWidth: 180,
    fontSize: '16px',
    minHeight: 64,
  },
});

const ReservesSystemSettingsPage: React.FC = () => {
  const { data: currentEmployee } = Employees.useCurrentEmployee();

  return (
    <PageContentWrap>
      <Grid item xl={12} lg={9} md={12} sm={12} xs={12}>
        <ReservesSystemSettings />
      </Grid>
      <Grid item xl={12} lg={9} md={12} sm={12} xs={12}>
        <ReservesSystemWidgets />
      </Grid>
      <Grid item xl={12} lg={9} md={12} sm={12} xs={12}>
        <ReservesSystemAdditionalNotifications />
      </Grid>
      <Grid item xl={12} lg={9} md={12} sm={12} xs={12}>
        <ReservesSystemAutoBooking />
      </Grid>
      <Grid item xl={12} lg={9} md={12} sm={12} xs={12}>
        <ReservesSystemTables />
      </Grid>
    </PageContentWrap>
  );
};

const AboutRestaurantInfoPage: React.FC = () => {
  const { data: currentEmployee } = Employees.useCurrentEmployee();

  useEffect(() => {
    document.body.style.background = '#fff';
    return () => {
      document.body.style.background = '#e7ebee';
    };
  }, [])

  return (
    <>
      <PageContentWrap>
        <Grid item lg={12} xs={12}>
          <ReservesSystemInfo />
        </Grid>
        <Grid item lg={12} xs={12}>
          <RestaurantCard />
        </Grid>
        <Grid item lg={12} xs={12}>
          <RestaurantActions />
        </Grid>
        {/*{currentEmployee && Employees.isAdmin(currentEmployee) && (*/}
        {/*    <Grid item xl={12} lg={9} md={12} sm={12} xs={12}>*/}
        {/*      <div style={{ paddingTop: 24 }}>*/}
        {/*        /!*<DeleteRestaurant />*!/*/}
        {/*      </div>*/}
        {/*    </Grid>*/}
        {/*)}*/}
      </PageContentWrap>
    </>
  );
};

const ReservesSystemSchemePage: React.FC = () => (
  <PageContentWrap>
    <Grid item xl={12} lg={9} md={12} sm={12} xs={12}>
      <ReservesSystemSchema />
    </Grid>
  </PageContentWrap>
);


type PageID = 'settings' | 'prepay' | 'scheme' | 'employees' | 'profile' | 'about';

const routeMap: {
  [view: string]: string
} = {
  about: Routers.about,
  settings: Routers.reservesSystem,
  prepay: Routers.prepay,
  scheme: Routers.restaurantScheme,
  employees: Routers.employees,
  profile: Routers.profile,
};

interface SettingsPageProps {
  view: PageID;
}

const SettingsPage: React.FC<SettingsPageProps> = ({ view }) => {
  const history = useHistory();
  const classes = useStyles();
  const onTabChange = useCallback((event: any, value: string) => {
    if (routeMap[value]) {
      history.push(routeMap[value]);
    }
  }, []);

  const { data: e } = Employees.useCurrentEmployee();
  const isRestaurantSettings = e && Employees.hasAccess(e, 'restaurantSettings:access');
  const isEmployeesPage = e && Employees.hasAccess(e, 'employees:access');

  return (
    <Fragment>
      {view !== 'profile' && (
        <PageHeaderWrap>
          <Tabs
            onChange={onTabChange}
            scrollButtons="off"
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            value={view}
            classes={{ root: classes.tabsRoot }}
          >
            {isRestaurantSettings && <Tab classes={{ root: classes.tabRoot }} label={locales.t('aboutRestaurant.title')} value="about" />}
            {isRestaurantSettings && <Tab classes={{ root: classes.tabRoot }} label={locales.t('reservesSystem.title')} value="settings" />}
            {isRestaurantSettings && <Tab classes={{ root: classes.tabRoot }} label={locales.t('prepay.title')} value="prepay" />}
            {isRestaurantSettings && <Tab classes={{ root: classes.tabRoot }} label={locales.t('restaurantSchema.title')} value="scheme" />}
            {isEmployeesPage && <Tab classes={{ root: classes.tabRoot }} label={locales.t('employees.title')} value="employees" />}

            {/* <Tab classes={{ root: classes.tabRoot }} label={locales.t('common.profile')} value="profile" /> */}
          </Tabs>
        </PageHeaderWrap>
      )}

      <CashBoxWarningCont />
      <NoHostessWarning />

      {view === 'about' && <AboutRestaurantInfoPage />}
      {view === 'settings' && <ReservesSystemSettingsPage />}
      {view === 'prepay' && <PrepaySettings />}
      {view === 'scheme' && <ReservesSystemSchemePage />}
      {view === 'employees' && <EmployeesPage />}
      {view === 'profile' && <ProfilePage />}
    </Fragment>
  );
};

export const AboutRestaurantPage: React.FC = () => (
  <SettingsPage view="about" />
);

export const ReservesSystemPage: React.FC = () => (
  <SettingsPage view="settings" />
);

export const ReservesSystemPrepaySettingsPage: React.FC = () => (
  <SettingsPage view="prepay" />
);

export const RestaurantSchemePage: React.FC = () => (
  <SettingsPage view="scheme" />
);

export const ReservesSystemEmployeesPage: React.FC = () => (
  <SettingsPage view="employees" />
);

export const ReservesSystemProfilePage: React.FC = () => (
  <SettingsPage view="profile" />
);
